import React, { useEffect, useMemo, useState } from "react";
import { MetaMaskButton, useMetaMask, switchToNetwork, sendNative, evmChainFor } from "../components/MetaMaskPanel";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { ArrowDownTrayIcon as ArrowDownToLine, ArrowUpTrayIcon as ArrowUpFromLine, CheckCircleIcon, ChevronDownIcon as ChevronDown, ChevronRightIcon as ChevronRight, QuestionMarkCircleIcon as CircleHelp, ClockIcon as Clock3, DocumentDuplicateIcon as Copy, LinkIcon, MagnifyingGlassIcon as Search, WalletIcon as WalletCards } from "@heroicons/react/24/outline";
import Header from "../components/Header";
import { useFaqs } from "../components/SiteConfigContext";
import Get from "../services/Get";
import Post from "../services/Post";
import { apiError, fetchAllPages, isApiFailure, marketAssetsToCatalog, unwrapList } from "../services/providerData";
import "./DepositCrypto.css";

const assetSymbol = (asset) => String(asset?.symbol ?? asset?.coin ?? asset?.asset ?? asset?.currency ?? asset?.coin_name ?? asset?.asset_code ?? "").toUpperCase();
const assetName = (asset) => asset?.name ?? asset?.coinName ?? asset?.coin_name ?? asset?.full_name ?? asset?.display_name ?? assetSymbol(asset);
const networkName = (network) => typeof network === "string" ? network : network?.network ?? network?.network_name ?? network?.chain ?? network?.name ?? network?.networkName ?? network?.network_code ?? "";
const networkAsset = (network) => {
  const value = network?.coin ?? network?.asset ?? network?.symbol ?? network?.currency ?? network?.coin_name ?? network?.coin_code ?? network?.coinCode ?? network?.asset_code ?? network?.asset_symbol ?? network?.assetSymbol ?? network?.coin_symbol ?? network?.coinSymbol ?? network?.token_symbol ?? network?.token;
  return String(typeof value === "object" ? value?.symbol ?? value?.code ?? value?.name ?? "" : value ?? "").toUpperCase();
};
const assetColor = (asset) => asset?.color ?? asset?.icon_color ?? "#8b93a1";
const assetLogo = (asset) => {
  const logo = asset?.logo_url ?? asset?.logoUrl ?? asset?.logo_uri ?? asset?.logoUri ?? asset?.icon_url ?? asset?.iconUrl ?? asset?.icon_path ?? asset?.image_url ?? asset?.imageUrl ?? asset?.image_uri ?? asset?.coin_icon ?? asset?.coinIcon ?? asset?.coin_logo ?? asset?.image ?? asset?.logo ?? asset?.icon;
  return (typeof logo === "string" ? logo : logo?.url ?? logo?.src) || (assetSymbol(asset) ? `https://assets.coincap.io/assets/icons/${assetSymbol(asset).toLowerCase()}@2x.png` : "");
};
const assetMonogram = (asset) => assetSymbol(asset).replace(/^\d+/, "").slice(0, 2) || "¤";
const readableNetworkName = (name) => ({ BSC: "BNB Smart Chain (BSC)", SOL: "Solana (SOL)", ETH: "Ethereum (ETH)", BTC: "Bitcoin (BTC)", TRX: "TRON (TRX)" }[String(name).toUpperCase()] ?? name);
const assetPriority = (asset) => ({ BTC: 0, USDT: 1, BNB: 2, ETH: 3, USDC: 4, SOL: 5 }[assetSymbol(asset)] ?? 100);

function AssetMark({ asset, className = "" }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = assetLogo(asset);
  return logo && !logoFailed ? <img className={`coin-mark coin-logo ${className}`} src={logo} alt="" loading="lazy" onError={() => setLogoFailed(true)} />
    : <span className={`coin-mark ${className}`} style={{ "--coin-color": assetColor(asset) }}>{assetMonogram(asset)}</span>;
}

const defaultQuickLinks = ["How to deposit crypto?", "Crypto deposit step-by-step guide", "Deposit hasn't arrived?"].map((question) => ({ question }));
const isDepositTransaction = (transaction) => {
  const type = String(transaction?.type ?? "").toLowerCase();
  return !type || /deposit|credit|inbound/.test(type);
};
const formatTransactionDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const shortTransactionValue = (value) => {
  const text = String(value ?? "");
  return text.length > 14 ? `${text.slice(0, 6)}...${text.slice(-5)}` : text || "—";
};

function DepositCrypto() {
  const quickLinks = useFaqs("deposit", defaultQuickLinks);
  const [assets, setAssets] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [assetQuery, setAssetQuery] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [assetMenuOpen, setAssetMenuOpen] = useState(false);
  const [networkMenuOpen, setNetworkMenuOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [networkLoading, setNetworkLoading] = useState(false);
  const [networkError, setNetworkError] = useState("");
  const [addressLoading, setAddressLoading] = useState(false);
  const [address, setAddress] = useState("");
  const [addressTag, setAddressTag] = useState("");
  const [addressWarning, setAddressWarning] = useState("");
  const [addressError, setAddressError] = useState("");
  const [addressCopied, setAddressCopied] = useState(false);
  const mm = useMetaMask();
  const [mmAmount, setMmAmount] = useState("");
  const [mmStatus, setMmStatus] = useState("");
  const payWithMetaMask = async () => {
    setMmStatus("");
    try {
      const from = mm.account || await mm.connect();
      if (!from) return;
      await switchToNetwork(networkName(selectedNetwork));
      const hash = await sendNative({ from, to: address, amount: mmAmount });
      setMmStatus(`Transaction sent: ${hash}`);
    } catch (err) {
      setMmStatus(err?.message || "MetaMask transaction failed.");
    }
  };
  const [claimHash, setClaimHash] = useState("");
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState("");

  useEffect(() => {
    let active = true;
    fetchAllPages(Get.ProviderCatalog, "assets")
      .then(async (catalogResult) => {
        if (catalogResult.items.length === 0 && !isApiFailure(catalogResult.response)) {
          const market = await Get.MarketAssets().catch(() => null);
          if (market && !isApiFailure(market)) catalogResult = { ...catalogResult, items: marketAssetsToCatalog(unwrapList(market, "assets")) };
        }
        if (!active) return;
        const { response: catalogResponse, items: catalogItems } = catalogResult;
        if (isApiFailure(catalogResponse)) setCatalogError(apiError(catalogResponse));
        else setAssets(catalogItems.sort((left, right) => assetPriority(left) - assetPriority(right) || assetSymbol(left).localeCompare(assetSymbol(right))));
      })
      .catch(() => { if (active) setCatalogError("Couldn't load assets and networks. Please sign in and try again."); })
      .finally(() => { if (active) setCatalogLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    Get.CustodyTransactions({ per_page: 25 })
      .then((response) => {
        if (!active) return;
        if (isApiFailure(response)) {
          const message = apiError(response);
          setRecentError(/unauthenticated|unauthorized/i.test(message) ? "Sign in to view recent deposits." : message);
          return;
        }
        setRecentTransactions(unwrapList(response, "transactions"));
      })
      .catch(() => { if (active) setRecentError("Couldn't load recent deposits. Please try again."); })
      .finally(() => { if (active) setRecentLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedAsset) {
      setNetworks([]);
      setNetworkError("");
      setNetworkLoading(false);
      return undefined;
    }

    if (selectedAsset.fromMarket) {
      setNetworks([]);
      setNetworkError("");
      setNetworkLoading(false);
      return undefined;
    }

    let active = true;
    setNetworks([]);
    setNetworkError("");
    setNetworkLoading(true);
    Get.ProviderNetworks({ coin: assetSymbol(selectedAsset), enabled_only: 1  })
      .then((response) => {
        if (!active) return;
        if (isApiFailure(response)) {
          setNetworkError(apiError(response));
          return;
        }
        const rows = unwrapList(response, "networks");
        const depositNetworks = rows.filter((network) => network.deposit_enabled === true || network.deposit_enabled === 1 || network.deposit_enabled === "1");
        setNetworks(depositNetworks);
      })
      .catch(() => { if (active) setNetworkError("Couldn't load deposit networks for this asset."); })
      .finally(() => { if (active) setNetworkLoading(false); });
    return () => { active = false; };
  }, [selectedAsset]);

  const filteredAssets = useMemo(() => assets.filter((asset) =>
    `${assetSymbol(asset)} ${assetName(asset)}`.toLowerCase().includes(assetQuery.toLowerCase())
  ), [assets, assetQuery]);

  const availableNetworks = useMemo(() => {
    if (!selectedAsset) return [];
    const symbol = assetSymbol(selectedAsset);
    const hasAssetAssociations = networks.some((network) => networkAsset(network));
    const matching = hasAssetAssociations
      ? networks.filter((network) => networkAsset(network) === symbol)
      : networks;
    const catalogNetworks = selectedAsset.networks ?? selectedAsset.network_list ?? selectedAsset.networkList ?? [];
    const items = networks.length ? matching : catalogNetworks.filter((network) => network.deposit_enabled === true || network.deposit_enabled === 1 || network.deposit_enabled === "1");
    const catalogByName = new Map(catalogNetworks.map((network) => [networkName(network).toUpperCase(), network]));
    return items.map((item) => {
      const name = networkName(item);
      const storedNetwork = catalogByName.get(name.toUpperCase());
      return typeof item === "string" ? { network: item, ...(storedNetwork ?? {}) } : { ...item, ...(storedNetwork ?? {}) };
    }).filter((network) => networkName(network));
  }, [networks, selectedAsset]);

  const visibleTransactions = recentTransactions.filter(isDepositTransaction);

  useEffect(() => {
    if (!addressCopied) return undefined;
    const timeout = window.setTimeout(() => setAddressCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [addressCopied]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setAddressCopied(true);
    } catch {
      setAddressError("Couldn't copy address. Please copy it manually.");
    }
  };

  const chooseAsset = (asset) => {
    setSelectedAsset(asset);
    setSelectedNetwork(null);
    setAssetMenuOpen(false);
    setNetworkMenuOpen(false);
    setAddress("");
    setAddressTag("");
    setAddressWarning("");
    setAddressError("");
  };

  const claimDeposit = async () => {
    const txHash = claimHash.trim();
    if (txHash.length < 10) return;
    setClaimLoading(true);
    setClaimResult(null);
    try {
      const response = await Post.ClaimDeposit({ tx_hash: txHash });
      const failure = response?.errors?.tx_hash?.[0] ?? (response?.status === "completed" ? "" : response?.message);
      if (failure) {
        setClaimResult({ ok: false, text: failure });
      } else {
        setClaimResult({ ok: true, text: `Deposit credited: ${response.amount}`, link: response.explorer_url });
        setClaimHash("");
        window.dispatchEvent(new Event("wallet:refresh"));
      }
    } catch {
      setClaimResult({ ok: false, text: "Couldn't verify the deposit. Please try again." });
    } finally {
      setClaimLoading(false);
    }
  };

  const requestAddress = async () => {
    if (!selectedAsset || !selectedNetwork) return;
    const assetId = selectedAsset.id ?? selectedAsset.asset_id;
    const networkId = selectedNetwork.id ?? selectedNetwork.network_id;
    if (assetId == null || networkId == null) {
      setAddressError("This asset or network is missing its database ID. Refresh the catalog and try again.");
      return;
    }
    setAddressLoading(true);
    setAddressError("");
    setAddress("");
    setAddressTag("");
    setAddressWarning("");
    try {
      const response = await Post.CreateDepositAddress({ asset_id: assetId, network_id: networkId, ...(selectedAsset.fromMarket ? { source: "market" } : {}) });
      const payload = response?.data?.data ?? response?.data ?? response;
      const depositAddress = payload?.address ?? payload?.deposit_address ?? payload?.wallet_address;
      if (response?.success === false || response?.status === false || response?.error || !depositAddress) {
        setAddressError(apiError(response));
      } else {
        setAddress(depositAddress);
        setAddressTag(payload?.address_tag ?? payload?.tag ?? "");
        setAddressWarning(payload?.warning ?? "");
      }
    } catch {
      setAddressError("Couldn't create a deposit address. Please sign in and try again.");
    } finally {
      setAddressLoading(false);
    }
  };

  return (
    <div className="deposit-page">
      <Header />
      {addressCopied && <div className="deposit-copy-toast" role="status" aria-live="polite"><CheckCircleIcon />Address Copied</div>}
      <div className="deposit-layout">
        <aside className="deposit-sidebar" aria-label="Wallet actions">
          <button className={`deposit-side-link ${claimOpen ? "" : "active"}`} type="button" onClick={() => setClaimOpen(false)}><ArrowDownToLine />Deposit Asset</button>
          <Link className="deposit-side-link" to="/withdrawCrypto"><ArrowUpFromLine />Withdraw Asset</Link>
          <button className={`deposit-side-link ${claimOpen ? "active" : ""}`} type="button" onClick={() => setClaimOpen(true)}><CheckCircleIcon />Claim Deposit</button>
          <button className="deposit-side-link" type="button"><WalletCards />Deposit Fiat<ChevronRight className="side-chevron" /></button>
          <button className="deposit-side-link" type="button"><WalletCards />Withdraw Fiat<ChevronRight className="side-chevron" /></button>
        </aside>

        <main className="deposit-main">
          <div className="deposit-content-grid" hidden={claimOpen}>
            <section className="deposit-steps" aria-label="Deposit steps">
              <div className={`deposit-step ${selectedAsset ? "is-complete" : "is-active"}`}>
                <span className="step-marker">1</span>
                <div className="step-body">
                  <h2>Select Asset</h2>
                  <div className="asset-picker">
                    <button className={`asset-select-trigger ${assetMenuOpen ? "is-open" : ""}`} type="button" aria-expanded={assetMenuOpen} onClick={() => setAssetMenuOpen(!assetMenuOpen)}>
                      {selectedAsset ? <><AssetMark asset={selectedAsset} /><strong>{assetSymbol(selectedAsset)}</strong><span>{assetName(selectedAsset)}</span></> : <><Search /><span>Select Asset</span></>}
                      <ChevronDown />
                    </button>
                    {assetMenuOpen && <div className="asset-select-popover">
                      <label className="asset-search"><Search /><input autoFocus value={assetQuery} onChange={(event) => setAssetQuery(event.target.value)} placeholder="Search" aria-label="Search assets" /></label>
                      {!assetQuery && selectedAsset && <div className="asset-history"><span className="popover-caption">Search History</span><button type="button" onClick={() => chooseAsset(selectedAsset)}><AssetMark asset={selectedAsset} />{assetSymbol(selectedAsset)}<small>{assetName(selectedAsset)}</small></button></div>}
                      <span className="popover-caption">Coin List</span>
                      <div className="asset-options">
                        {catalogLoading && <span className="asset-empty">Loading available assets…</span>}
                        {!catalogLoading && filteredAssets.map((asset, index) => (
                          <button key={`${assetSymbol(asset)}-${index}`} type="button" title={`${assetName(asset)} (${assetSymbol(asset)})`} onClick={() => chooseAsset(asset)} className={`asset-option ${selectedAsset === asset ? "selected" : ""}`}>
                            <AssetMark asset={asset} /><strong>{assetSymbol(asset)}</strong><span>{assetName(asset)}</span>
                          </button>
                        ))}
                        {!catalogLoading && filteredAssets.length === 0 && <span className="asset-empty">{catalogError || "No available assets found"}</span>}
                      </div>
                      {catalogError && filteredAssets.length > 0 && <p className="api-inline-error">{catalogError}</p>}
                    </div>}
                  </div>
                </div>
              </div>

              <div className={`deposit-step ${selectedAsset ? "is-active" : "is-disabled"}`}>
                <span className="step-marker">2</span>
                <div className="step-body">
                  <h2>Select Network</h2>
                  <div className="network-picker">
                    <button className="network-select-trigger" type="button" disabled={!selectedAsset || !availableNetworks.length} aria-expanded={networkMenuOpen} onClick={() => setNetworkMenuOpen(!networkMenuOpen)}>
                      <span>{selectedNetwork ? networkName(selectedNetwork) : selectedAsset ? networkLoading ? "Loading networks…" : "Select network" : "Select network"}</span><ChevronDown />
                    </button>
                    {networkMenuOpen && <div className="network-select-popover">
                      <p className="network-notice">Please note that only supported networks are shown. If you deposit on another network, your assets may be lost.</p>
                      {availableNetworks.map((network) => {
                        const confirmations = network.confirmations ?? network.confirmation_count ?? network.confirmationCount;
                        return <button type="button" key={network.id ?? network.network_id ?? networkName(network)} onClick={() => { setSelectedNetwork(network); setNetworkMenuOpen(false); setAddress(""); setAddressTag(""); setAddressWarning(""); setAddressError(""); }} className={`network-option ${selectedNetwork === network ? "selected" : ""}`}>
                          <span><strong>{networkName(network)}</strong><small>{readableNetworkName(networkName(network))}</small></span>
                          <small>{confirmations ? `${confirmations} Confirmation${confirmations === 1 ? "" : "s"}` : "Deposit network"}</small>
                        </button>;
                      })}
                    </div>}
                  </div>
                  {!selectedAsset && <p className="step-hint">Select an asset first.</p>}
                  {selectedAsset && !networkLoading && !availableNetworks.length && <p className="step-hint">{networkError || "No deposit-enabled networks are available for this asset."}</p>}
                </div>
              </div>

              <div className={`deposit-step ${selectedNetwork ? "is-active" : "is-disabled"}`}>
                <span className="step-marker">3</span>
                <div className="step-body">
                  <h2>Deposit Address</h2>
                  {selectedNetwork ? <div className="address-placeholder">
                    {address && <QRCodeSVG className="deposit-address-qr" value={address} size={72} bgColor="#ffffff" fgColor="#17191f" level="M" />}
                    <div className="address-copy"><strong>{address ? "Deposit Address" : "Create a deposit address"}</strong><div className={address ? "deposit-address-line" : ""}><p className={address ? "deposit-address-value" : ""}>{address || `${assetSymbol(selectedAsset)} on ${networkName(selectedNetwork)}`}</p>{address && <button className="address-copy-button" type="button" onClick={copyAddress} aria-label={addressCopied ? "Address copied" : "Copy address"} title={addressCopied ? "Address copied" : "Copy address"}>{addressCopied ? <CheckCircleIcon className="address-copy-success-icon" /> : <Copy />}</button>}</div>{addressTag && <p>Address tag: <strong>{addressTag}</strong></p>}{addressWarning && <p className="deposit-address-warning">{addressWarning}</p>}{addressError && <p className="api-inline-error">{addressError}</p>}{address && evmChainFor(networkName(selectedNetwork)) && <div style={{ marginTop: 12 }}><MetaMaskButton account={mm.account} onConnect={mm.connect} /> <input value={mmAmount} onChange={(e) => setMmAmount(e.target.value)} placeholder="Amount" inputMode="decimal" style={{ marginLeft: 8, padding: 6, width: 110, color: "#eaecef", background: "#181a20", border: "1px solid #303844", borderRadius: 5, caretColor: "#f0b90b" }} /> <button type="button" onClick={payWithMetaMask} disabled={!mmAmount} style={{ marginLeft: 8, padding: "6px 12px" }}>Deposit via MetaMask</button>{(mmStatus || mm.error) && <p className="api-inline-error" style={{ wordBreak: "break-all" }}>{mmStatus || mm.error}</p>}<p style={{ fontSize: 12, opacity: 0.7 }}>Sends the network's native coin. For tokens, copy the address above.</p></div>}
                      {!address && <button className="address-create-button" type="button" onClick={requestAddress} disabled={addressLoading}>{addressLoading ? "Generating…" : "Get Address"}</button>}
                    </div>
                  </div> : <p className="step-hint">Select a network to continue.</p>}
                </div>
              </div>
            </section>

            <aside className="deposit-faq">
              <h2>FAQ</h2>
              <button type="button" className="faq-video"><span className="video-thumb"><span className="play-icon">▶</span></span><span><strong>How to deposit crypto?</strong><small>4:10 min</small></span></button>
              {quickLinks.slice(1).map((item) => <button type="button" className="faq-link" key={item.question}>{item.question}<ChevronRight /></button>)}
              <Link className="faq-support" to="/support-center"><CircleHelp />Need help? Visit Support Center</Link>
            </aside>
          </div>

          {claimOpen && <section className="deposit-claim">
            <h2>Deposit sent but balance not updated?</h2>
            <p>Paste your transaction hash (TxID) to verify and credit it.</p>
            <div className="deposit-claim-row">
              <input value={claimHash} onChange={(event) => setClaimHash(event.target.value)} placeholder="Transaction hash (TxID)" />
              <button type="button" onClick={claimDeposit} disabled={claimLoading || claimHash.trim().length < 10}>{claimLoading ? "Checking…" : "Claim deposit"}</button>
            </div>
            {claimResult && <p className={claimResult.ok ? "deposit-claim-ok" : "api-inline-error"}>{claimResult.text} {claimResult.link && <a href={claimResult.link} target="_blank" rel="noreferrer">View transaction</a>}</p>}
          </section>}

          <section className="recent-deposits">
            <div className="recent-heading"><div><h2>Recent Deposits</h2><span><Clock3 />Your latest deposit activity</span></div><Link to="/transaction-history">More <ChevronRight /></Link></div>
            <div className="recent-transaction-list">
              {visibleTransactions.map((transaction) => {
                  const asset = transaction.asset ?? transaction.coin ?? transaction.currency ?? {};
                  const symbol = assetSymbol(asset) || String(transaction.asset_symbol ?? transaction.coin_symbol ?? "").toUpperCase();
                  const status = String(transaction.status ?? "Pending");
                  const statusClass = status.toLowerCase().replace(/[^a-z0-9-]/g, "-");
                  const amount = transaction.amount ?? transaction.quantity ?? transaction.value ?? "—";
                  const network = networkName(transaction.network ?? transaction.network_name ?? transaction.chain) || "—";
                  const address = transaction.address ?? transaction.deposit_address ?? transaction.wallet_address ?? transaction.destination ?? transaction.to_address ?? "";
                  const txid = transaction.txid ?? transaction.tx_id ?? transaction.tx_hash ?? transaction.transaction_hash ?? transaction.transaction_id ?? "";
                  const addressExplorer = transaction.address_explorer_url ?? transaction.address_url ?? "";
                  const txExplorer = transaction.tx_explorer_url ?? transaction.transaction_url ?? transaction.explorer_url ?? "";
                  const depositWallet = transaction.deposit_wallet ?? transaction.wallet_name ?? transaction.wallet_type ?? transaction.wallet ?? "Spot Wallet";
                  return <article className="recent-transaction" key={transaction.id ?? transaction.uuid ?? txid}>
                    <div className="transaction-primary">
                      <AssetMark asset={typeof asset === "string" ? { symbol: asset } : { ...asset, symbol }} />
                      <span className="transaction-amount">{amount} {symbol}</span>
                      <span className={`transaction-status status-${statusClass}`}>{status}</span>
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-detail"><span>Date</span><strong>{formatTransactionDate(transaction.completed_at ?? transaction.created_at ?? transaction.updated_at)}</strong></div>
                      <div className="transaction-detail"><span>Network</span><strong>{network}</strong></div>
                      <div className="transaction-detail"><span>Address</span><strong title={address || undefined}>{shortTransactionValue(address)}</strong>
                        {addressExplorer && <a className="transaction-icon-link" href={addressExplorer} target="_blank" rel="noreferrer" aria-label="Open address in explorer"><LinkIcon /></a>}
                        {address && <button className="transaction-icon-link" type="button" onClick={() => navigator.clipboard?.writeText(address)} aria-label="Copy address"><Copy /></button>}
                      </div>
                      <div className="transaction-detail"><span>TxID</span><strong title={txid || undefined}>{shortTransactionValue(txid)}</strong>
                        {txExplorer && <a className="transaction-icon-link" href={txExplorer} target="_blank" rel="noreferrer" aria-label="Open transaction in explorer"><LinkIcon /></a>}
                        {txid && <button className="transaction-icon-link" type="button" onClick={() => navigator.clipboard?.writeText(txid)} aria-label="Copy transaction ID"><Copy /></button>}
                      </div>
                      <div className="transaction-detail"><span>Deposit wallet</span><strong>{depositWallet}</strong></div>
                    </div>
                  </article>;
                })}
              {(recentLoading || recentError || visibleTransactions.length === 0) && <div className="deposit-empty">
                <div className="empty-icon"><Copy /></div>
                <strong>{recentLoading ? "Loading recent deposits…" : recentError || "No recent crypto deposits"}</strong>
                {!recentLoading && !recentError && <span>Your deposit history will show here.</span>}
              </div>}
            </div>
          </section>
        </main>
      </div>
      <div className="deposit-legal-footer"><span>Tillionx© 2026</span><button type="button">Cookie Preferences</button></div>
    </div>
  );
}

export default DepositCrypto;
